import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  Empty,
  Row,
  Col,
  Card,
  Select,
  Button,
  Modal,
  Spin,
  DatePicker,
} from "antd";
import { BarsOutlined } from "@ant-design/icons";
import { useHistory, useLocation } from "react-router-dom";
import moment from "moment";
import RequestHandler from "../../util/RequestHandler";
import { ColumnHelper } from "../../util/TableHelper";

const PAGE_SIZE = 10;

const getDefaultNonCreditFilters = () => ({
  page: 1,
  pageSize: PAGE_SIZE,
  startDate: moment().startOf("month"),
  endDate: moment().endOf("day"),
  debitCreditIndicator: "",
});

const BkmTenantCardList = () => {
  const [tableData, setTableData] = useState({
    data: [],
    loading: true,
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [statementCurrencies, setStatementCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState(undefined);
  const [creditCardDetail, setCreditCardDetail] = useState({
    loading: false,
    data: null,
  });
  const [nonCreditCardDetail, setNonCreditCardDetail] = useState({
    loading: false,
    data: null,
  });
  const [nonCreditTransactions, setNonCreditTransactions] = useState({
    loading: false,
    data: [],
    total: 0,
  });
  const [nonCreditFilters, setNonCreditFilters] = useState(
    getDefaultNonCreditFilters(),
  );
  const [draftFilters, setDraftFilters] = useState(
    getDefaultNonCreditFilters(),
  );

  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    getPageData();
  }, []);

  useEffect(() => {
    if (
      !selectedCard ||
      !isCreditCard(selectedCard) ||
      isVirtualCard(selectedCard) ||
      !selectedCard.hasCardDetailPermission ||
      !selectedCurrency
    ) {
      setCreditCardDetail({ loading: false, data: null });
      return;
    }

    getCreditCardDetail(selectedCard.id, selectedCurrency);
  }, [selectedCard, selectedCurrency]);

  useEffect(() => {
    if (
      !selectedCard ||
      isCreditCard(selectedCard) ||
      isVirtualCard(selectedCard) ||
      !selectedCard.hasCardDetailPermission ||
      !selectedCurrency
    ) {
      setNonCreditCardDetail({ loading: false, data: null });
      return;
    }

    getNonCreditCardDetail(selectedCard.id, selectedCurrency);
  }, [selectedCard, selectedCurrency]);

  useEffect(() => {
    if (
      !selectedCard ||
      isCreditCard(selectedCard) ||
      isVirtualCard(selectedCard) ||
      !selectedCard.hasCardTransactionPermission ||
      !selectedCurrency
    ) {
      setNonCreditTransactions({
        loading: false,
        data: [],
        total: 0,
      });
      return;
    }

    getNonCreditCardTransactions(
      selectedCard.id,
      selectedCurrency,
      nonCreditFilters,
    );
  }, [selectedCard, selectedCurrency, nonCreditFilters]);

  const getPageData = async () => {
    setTableData((prevState) => ({
      ...prevState,
      loading: true,
    }));

    const checkCanceledResponse = await RequestHandler.post(
      `/BkmBankInfo/CheckCanceledConsentsAsync`,
    );

    if (checkCanceledResponse === undefined) return;

    const cardsResponse = await RequestHandler.get(`/TenantCard`);
    const normalizedCards = normalizeTenantCards(cardsResponse);

    setTableData({
      data: normalizedCards,
      loading: false,
    });

    const restoreSelectedCardId = location.state?.restoreSelectedCardId;
    const restoreStatementCurrency = location.state?.restoreStatementCurrency;

    if (restoreSelectedCardId) {
      const restoredCard = normalizedCards.find(
        (item) => item.id === restoreSelectedCardId,
      );

      if (restoredCard) {
        const currencies = getStatementCurrencies(restoredCard);
        const restoredCurrency =
          restoreStatementCurrency && currencies.includes(restoreStatementCurrency)
            ? restoreStatementCurrency
            : currencies[0];
        const defaultFilters = getDefaultNonCreditFilters();

        setSelectedRowKeys([restoredCard.id]);
        setSelectedCard(restoredCard);
        setStatementCurrencies(currencies);
        setSelectedCurrency(restoredCurrency);
        setNonCreditFilters(defaultFilters);
        setDraftFilters(defaultFilters);
      }

      history.replace({
        pathname: location.pathname,
        state: {},
      });
    }
  };

  const normalizeTenantCards = (response) => {
    const cards = Array.isArray(response)
      ? response
      : Array.isArray(response?.value)
        ? response.value
        : Array.isArray(response?.data)
          ? response.data
          : [];

    return cards.map((item) => {
      const cardDetails = getField(item, ["cardDetails", "CardDetails"], []);
      const tenantCardStatementTypes = getField(
        item,
        ["tenantCardStatementTypes", "TenantCardStatementTypes"],
        [],
      );

      return {
        id: getField(item, ["id", "Id"]),
        bankName: getField(item, ["bankName", "BankName"], ""),
        cardNumber: getField(item, ["cardNumber", "CardNumber"], ""),
        cardHolderName: getField(
          item,
          ["cardHolderName", "CardHolderName"],
          "",
        ),
        cardName: getField(
          item,
          [
            "cardProductName",
            "CardProductName",
            "cardName",
            "CardName",
            "cardAlias",
            "CardAlias",
            "cardNickName",
            "CardNickName",
          ],
          "",
        ),
        cardProductName: getField(
          item,
          ["cardProductName", "CardProductName"],
          "",
        ),
        cardSchemeCode: getField(
          item,
          ["cardSchemeCode", "CardSchemeCode"],
          "",
        ),
        cardTypeCode: getField(item, ["cardTypeCode", "CardTypeCode"], ""),
        cardSubTypeCode: getField(
          item,
          ["cardSubTypeCode", "CardSubTypeCode"],
          "",
        ),
        cardStatusCode: getField(
          item,
          ["cardStatusCode", "CardStatusCode"],
          "",
        ),
        hasCardListPermission: !!getField(item, [
          "hasCardListPermission",
          "HasCardListPermission",
        ]),
        hasCardDetailPermission: !!getField(item, [
          "hasCardDetailPermission",
          "HasCardDetailPermission",
        ]),
        hasCardTransactionPermission: !!getField(item, [
          "hasCardTransactionPermission",
          "HasCardTransactionPermission",
        ]),
        statementCurrency: getField(
          item,
          ["statementCurrency", "StatementCurrency"],
          "",
        ),
        tenantCardStatementTypes,
        cardDetails: Array.isArray(cardDetails) ? cardDetails : [],
      };
    });
  };

  const getField = (source, fieldNames, fallback = undefined) => {
    for (const fieldName of fieldNames) {
      if (source?.[fieldName] !== undefined && source?.[fieldName] !== null) {
        return source[fieldName];
      }
    }

    return fallback;
  };

  const getFirstValue = (sources, fieldNames, fallback = "") => {
    for (const source of sources) {
      const value = getField(source, fieldNames);
      if (value !== undefined && value !== null && value !== "") {
        return value;
      }
    }

    return fallback;
  };

  const isCreditCard = (card) => card?.cardTypeCode === "K";

  const isVirtualCard = (card) => card?.cardSubTypeCode === "S";

  const isOtherCardType = (card) =>
    !isCreditCard(card) &&
    !isVirtualCard(card) &&
    (card?.cardTypeCode === "B" || card?.cardTypeCode === "P");

  const getStatementCurrencies = (card) => {
    const rawValues = [];
    const statementTypes = card?.tenantCardStatementTypes || [];
    const detailList = card?.cardDetails || [];

    statementTypes.forEach((item) => {
      if (typeof item === "string") {
        rawValues.push(item);
        return;
      }

      rawValues.push(
        getField(item, ["statementCurrency", "StatementCurrency"]),
        getField(item, ["currencyCode", "CurrencyCode"]),
        getField(item, ["currency", "Currency"]),
      );
    });

    rawValues.push(card?.statementCurrency);

    detailList.forEach((item) => {
      rawValues.push(
        getField(item, ["statementCurrency", "StatementCurrency"]),
      );
    });

    const normalized = [...new Set(rawValues.filter(Boolean))];

    return normalized.length > 0 ? normalized : ["TRY"];
  };

  const normalizeDetailResponse = (response) => {
    if (!response) return null;
    if (response.data && typeof response.data === "object")
      return response.data;

    return response;
  };

  const normalizeTransactionResponse = (response) => {
    const rows = Array.isArray(response)
      ? response
      : Array.isArray(response?.value)
        ? response.value
        : Array.isArray(response?.data)
          ? response.data
          : [];

    const total =
      response?.totalCount ??
      response?.TotalCount ??
      response?.["@odata.count"] ??
      rows.length;

    return {
      data: rows,
      total,
    };
  };

  const getFilterDateTimeValue = (dateValue, boundary) => {
    if (!dateValue) return null;

    const parsedDate = moment.isMoment(dateValue)
      ? dateValue.clone()
      : moment(dateValue, "YYYY-MM-DD", true);
    if (!parsedDate.isValid()) return null;

    return boundary === "start"
      ? parsedDate.startOf("day").format("YYYY-MM-DDTHH:mm:ss")
      : parsedDate.endOf("day").format("YYYY-MM-DDTHH:mm:ss");
  };

  const normalizeNonCreditFilters = (filters, pageOverride) => {
    const defaultFilters = getDefaultNonCreditFilters();

    return {
      ...defaultFilters,
      ...filters,
      startDate: filters?.startDate || defaultFilters.startDate.clone(),
      endDate: filters?.endDate || defaultFilters.endDate.clone(),
      debitCreditIndicator: filters?.debitCreditIndicator ?? "",
      page: pageOverride ?? filters?.page ?? defaultFilters.page,
    };
  };

  const buildNonCreditTransactionFilterPayload = (
    filters,
    tenantCardId,
    currency,
  ) => {
    const normalizedFilters = normalizeNonCreditFilters(filters);

    return {
      tenantCardId,
      statementCurrency: currency,
      startDate: getFilterDateTimeValue(normalizedFilters.startDate, "start"),
      endDate: getFilterDateTimeValue(normalizedFilters.endDate, "end"),
      debitCreditIndicator: normalizedFilters.debitCreditIndicator,
      firstData: 0,
      count: normalizedFilters.pageSize,
      page: normalizedFilters.page,
      order: "transactionDate desc",
      currentPeriodOnly: false,
      periodEndMonth: 0,
      periodEndYear: 0,
    };
  };

  const getCreditCardDetail = async (tenantCardId, currency) => {
    setCreditCardDetail({
      loading: true,
      data: null,
    });

    const syncDetailResponse = await RequestHandler.get(
      `/BkmCardSync/SyncCardDetail/${tenantCardId}?statementCurrency=${currency}`,
    );

    if (syncDetailResponse === undefined) return;

    const response = await RequestHandler.get(
      `/CardDetail/GetByTenantCardId/${tenantCardId}/${currency}`,
    );

    setCreditCardDetail({
      loading: false,
      data: normalizeDetailResponse(response),
    });
  };

  const getNonCreditCardDetail = async (tenantCardId, currency) => {
    setNonCreditCardDetail({
      loading: true,
      data: null,
    });

    const syncDetailResponse = await RequestHandler.get(
      `/BkmCardSync/SyncCardDetail/${tenantCardId}?statementCurrency=${currency}`,
    );

    if (syncDetailResponse === undefined) return;

    const detailResponse = await RequestHandler.get(
      `/CardDetail/GetByTenantCardId/${tenantCardId}/${currency}`,
    );

    setNonCreditCardDetail({
      loading: false,
      data: normalizeDetailResponse(detailResponse),
    });
  };

  const getNonCreditCardTransactions = async (
    tenantCardId,
    currency,
    filters,
  ) => {
    setNonCreditTransactions((prevState) => ({
      ...prevState,
      loading: true,
    }));

    const syncTransactionsResponse = await RequestHandler.get(
      `/BkmCardSync/SyncCardTransactions/${tenantCardId}?periodValue=0&statementCurrency=${currency}`,
    );

    if (syncTransactionsResponse === undefined) return;

    const transactionResponse = await RequestHandler.post(
      `/CardTransaction/GetListByFilter`,
      buildNonCreditTransactionFilterPayload(filters, tenantCardId, currency),
      false,
    );

    const normalized = normalizeTransactionResponse(transactionResponse);

    setNonCreditTransactions({
      loading: false,
      data: normalized.data,
      total: normalized.total,
    });
  };

  const sanitizeSerializable = (value) => {
    try {
      return JSON.parse(JSON.stringify(value ?? null));
    } catch (error) {
      return null;
    }
  };

  const redirectToCardTransactions = (card, detailData, currency) => {
    history.push({
      pathname: "/card-transactions",
      state: {
        selectedCard: sanitizeSerializable(card),
        cardDetailData: sanitizeSerializable(detailData),
        statementCurrency: currency || null,
      },
    });
  };

  const redirectToCardStatementDetail = (card, detailData, currency) => {
    history.push({
      pathname: "/card-statement-detail",
      state: {
        selectedCard: sanitizeSerializable(card),
        cardDetailData: sanitizeSerializable(detailData),
        statementCurrency: currency || null,
      },
    });
  };

  const applySelectableCardState = (card, nextKeys, currencies) => {
    const defaultFilters = getDefaultNonCreditFilters();

    setSelectedRowKeys(nextKeys);
    setSelectedCard(card);
    setStatementCurrencies(currencies);
    setSelectedCurrency(currencies[0]);
    setNonCreditFilters(defaultFilters);
    setDraftFilters(defaultFilters);
  };

  const handleDraftFilterChange = (fieldName, value) => {
    const defaultFilters = getDefaultNonCreditFilters();

    setDraftFilters((prevState) => ({
      ...prevState,
      [fieldName]:
        (fieldName === "startDate" || fieldName === "endDate") && !value
          ? defaultFilters[fieldName]
          : value,
    }));
  };

  const handleSearchNonCreditTransactions = () => {
    setNonCreditFilters(
      normalizeNonCreditFilters(
        {
          startDate: draftFilters.startDate,
          endDate: draftFilters.endDate,
          debitCreditIndicator: draftFilters.debitCreditIndicator,
        },
        1,
      ),
    );
  };

  const handleResetNonCreditTransactions = () => {
    const defaultFilters = getDefaultNonCreditFilters();

    setDraftFilters(defaultFilters);
    setNonCreditFilters(defaultFilters);
  };

  const handleSelectCard = (card, nextKeys) => {
    const currencies = getStatementCurrencies(card);

    if (isVirtualCard(card)) {
      if (card.hasCardTransactionPermission) {
        applySelectableCardState(card, nextKeys, currencies);
        redirectToCardTransactions(card, null, currencies[0]);
        return;
      }

      Modal.info({
        title: "Kart Hareketleri İzni Bulunamadı",
        content:
          "Kart Hareketleri izniniz bulunmamaktadır. Mevcut Rıza güncelleme akışlarından rıza izinlerini güncelleyebilirsiniz.",
      });
      return;
    }

    if (!card.hasCardDetailPermission) {
      if (isCreditCard(card)) {
        if (card.hasCardTransactionPermission) {
          Modal.confirm({
            title: "Kart Detay Bilgileri Görüntüleme İzni Bulunamadı",
            content:
              "Kart detay bilgileri görüntüleme izniniz bulunmadığında tekrardan rıza güncelleyip izinlerinizi alabilirsiniz.",
            okText: "Tamam",
            cancelText: "Vazgeç",
            onOk: () => redirectToCardTransactions(card, null, currencies[0]),
          });
        }

        return;
      }

      if (isOtherCardType(card) && card.hasCardTransactionPermission) {
        applySelectableCardState(card, nextKeys, currencies);
        return;
      }

      if (card.hasCardTransactionPermission) {
        Modal.confirm({
          title: "Ayrıntılı Kart Bilgisi İzni Bulunamadı",
          content:
            "Ayrıntılı Kart Bilgisi izniniz bulunmadığı için Kart Hareketleri ekranına yönlendiriliyorsunuz.",
          okText: "Tamam",
          cancelText: "Vazgeç",
          onOk: () => redirectToCardTransactions(card, null, currencies[0]),
        });
        return;
      }

      Modal.info({
        title: "Ayrıntılı Kart Bilgisi İzni Bulunamadı",
        content:
          "Ayrıntılı Kart Bilgisi izniniz bulunmamaktadır. Mevcut Rıza güncelleme akışlarından rıza izinlerini güncelleyebilirsiniz.",
      });
      return;
    }

    applySelectableCardState(card, nextKeys, currencies);
  };

  const getCardTypeLabel = (row) => {
    if (isVirtualCard(row)) return "Sanal Kart";

    const map = {
      K: "Kredi Kartı",
      B: "Banka Kartı",
      P: "Ön Ödemeli Kart",
    };

    return map[row.cardTypeCode] || row.cardTypeCode || "";
  };

  const getCardStatusLabel = (value) => {
    const map = {
      A: "Aktif",
      P: "Pasif",
      K: "Kapalı",
      I: "İptal",
    };

    return map[value] || value || "";
  };

  const getCardPreviewValues = (card, detailData) => {
    const firstDetail = Array.isArray(card?.cardDetails)
      ? card.cardDetails[0]
      : null;
    const movementDataSources = [detailData, firstDetail, card];

    return {
      currentBalance: getFirstValue(
        [detailData, firstDetail, card],
        [
          "currentBalance",
          "CurrentBalance",
          "availableBalance",
          "AvailableBalance",
          "balanceAmount",
          "BalanceAmount",
        ],
        "",
      ),
      periodSpend: getMovementAmount(movementDataSources),
    };
  };

  const getDetailField = (fieldNames, fallback = "") =>
    getFirstValue([creditCardDetail.data], fieldNames, fallback);

  const getMovementAmount = (sources) =>
    getFirstValue(
      sources,
      [
        "periodMovementTotalAmount",
        "PeriodMovementTotalAmount",
        "periodTransactionTotal",
        "PeriodTransactionTotal",
        "periodSpendingAmount",
        "PeriodSpendingAmount",
        "periodExpenseAmount",
        "PeriodExpenseAmount",
      ],
      "",
    );

  const getMovementCurrency = (sources) =>
    getFirstValue(
      sources,
      [
        "periodMovementTotalCurrency",
        "PeriodMovementTotalCurrency",
        "statementCurrency",
        "StatementCurrency",
      ],
      "",
    );

  const formatAmountWithCurrency = (amount, currency) => {
    if (amount === "" || amount === null || amount === undefined) {
      return "";
    }

    const formattedAmount = ColumnHelper.renderDecimal(amount);

    return currency ? `${formattedAmount} ${currency}` : formattedAmount;
  };

  const formatDetailDate = (value) => {
    if (!value) return "";

    const parsedDate = moment(value);

    if (!parsedDate.isValid()) return "";

    const year = parsedDate.year();
    const month = parsedDate.month();
    const day = parsedDate.date();

    const isMinDate = year === 1 && month === 0 && day === 1;
    const isUnixEpochDate = year === 1970 && month === 0 && day === 1;

    if (isMinDate || isUnixEpochDate) {
      return "";
    }

    return parsedDate.format("DD.MM.YYYY");
  };

  const getDetailAmountWithCurrency = (
    amountFieldNames,
    currencyFieldNames,
    fallback = "",
  ) => {
    const amount = getDetailField(amountFieldNames);

    if (amount === "" || amount === null || amount === undefined) {
      return fallback;
    }

    const currency = getDetailField(currencyFieldNames);

    return formatAmountWithCurrency(amount, currency);
  };

  const getCreditCardRewardPointRows = () => {
    const pointList = getFirstValue(
      [creditCardDetail.data],
      [
        "cardDetailRewardPoints",
        "CardDetailRewardPoints",
        "cardPoints",
        "CardPoints",
        "points",
        "Points",
      ],
      [],
    );

    if (!Array.isArray(pointList)) {
      return [];
    }

    return pointList
      .map((item, index) => {
        const label = getFirstValue(
          [item],
          [
            "pointType",
            "PointType",
            "rewardPointTypeName",
            "RewardPointTypeName",
            "rewardTypeName",
            "RewardTypeName",
            "name",
            "Name",
          ],
          "",
        );
        const amount = getFirstValue(
          [item],
          [
            "pointValue",
            "PointValue",
            "rewardPointAmount",
            "RewardPointAmount",
            "amount",
            "Amount",
            "pointAmount",
            "PointAmount",
          ],
          "",
        );
        const currency = getFirstValue(
          [item],
          ["currencyCode", "CurrencyCode"],
          "",
        );

        return {
          key: `reward-point-${index}`,
          label,
          value: formatAmountWithCurrency(amount, currency),
        };
      })
      .filter((item) => item.label || item.value);
  };

  const simpleCardPreview = useMemo(
    () =>
      selectedCard
        ? getCardPreviewValues(selectedCard, nonCreditCardDetail.data)
        : null,
    [selectedCard, nonCreditCardDetail.data],
  );

  const shouldRenderCreditCardPanel =
    selectedCard &&
    isCreditCard(selectedCard) &&
    !isVirtualCard(selectedCard) &&
    selectedCard.hasCardDetailPermission;

  const shouldRenderBasicCardPanel =
    selectedCard &&
    !isCreditCard(selectedCard) &&
    !isVirtualCard(selectedCard) &&
    selectedCard.hasCardDetailPermission;

  const isOtherCard =
    selectedCard &&
    isOtherCardType(selectedCard);

  const shouldRenderOtherCardSummary =
    isOtherCard && selectedCard?.hasCardDetailPermission;

  const shouldRenderOtherCardTransactions =
    isOtherCard && selectedCard?.hasCardTransactionPermission;

  const tableColumns = [
    {
      title: "Kurum Adı",
      dataIndex: "bankName",
      key: "bankName",
    },
    {
      title: "Kart No",
      dataIndex: "cardNumber",
      key: "cardNumber",
    },
    {
      title: "Kart Adı",
      dataIndex: "cardName",
      key: "cardName",
    },
    {
      title: "Kart Tipi",
      dataIndex: "cardTypeCode",
      key: "cardTypeCode",
      render: (_, row) => getCardTypeLabel(row),
    },
    {
      title: "Durum",
      dataIndex: "cardStatusCode",
      key: "cardStatusCode",
      render: (data) => getCardStatusLabel(data),
    },
  ];

  const transactionColumns = [
    {
      title: "İşlem Tarihi",
      dataIndex: "transactionDate",
      render: (_, row) => {
        const dateValue = getFirstValue(
          [row],
          [
            "transactionDate",
            "TransactionDate",
            "eventDate",
            "EventDate",
            "createDate",
            "CreateDate",
          ],
          "",
        );

        return dateValue ? moment(dateValue).format("DD.MM.YYYY") : "";
      },
    },
    {
      title: "Açıklama",
      dataIndex: "description",
      render: (_, row) =>
        getFirstValue(
          [row],
          [
            "transactionDescription",
            "TransactionDescription",
            "description",
            "Description",
          ],
          "",
        ),
    },
    {
      title: "İşlem Tutarı",
      dataIndex: "transactionAmount",
      render: (_, row) => {
        const amount = getFirstValue(
          [row],
          ["transactionAmount", "TransactionAmount", "amount", "Amount"],
          "",
        );

        return amount === "" ? "" : ColumnHelper.renderDecimal(amount);
      },
    },
  ];

  const rowSelection = {
    type: "radio",
    selectedRowKeys: selectedRowKeys,
    onChange: (nextKeys, selectedRows) => {
      const row = selectedRows?.[0];

      if (row) {
        handleSelectCard(row, nextKeys);
      }
    },
  };

  const renderInfoTable = (title, columns, dataSource, extraProps = {}) => {
    const { footerAction = null, ...tableProps } = extraProps;

    return (
      <Card
        size="small"
        title={title}
        style={{ borderRadius: 6, marginBottom: 12 }}
        headStyle={{ fontWeight: 600 }}
        bodyStyle={{ padding: 0 }}
      >
        <Table
          size="small"
          columns={columns}
          dataSource={dataSource}
          rowKey={(row, index) => row?.id || row?.Id || index}
          pagination={false}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Veri Yok"
              />
            ),
          }}
          {...tableProps}
        />
        {footerAction && (
          <div style={{ padding: "12px 16px", borderTop: "1px solid #f0f0f0" }}>
            <Row justify="end">{footerAction}</Row>
          </div>
        )}
      </Card>
    );
  };

  const renderKeyValueInfoTable = (title, rows, extraProps = {}) =>
    renderInfoTable(
      title,
      [
        {
          title: "Bilgi",
          dataIndex: "label",
          key: "label",
          width: "55%",
        },
        {
          title: "Değer",
          dataIndex: "value",
          key: "value",
        },
      ],
      rows,
      extraProps,
    );

  return (
    <>
      <Row>
        <Col span={24}>
          <p style={{ fontSize: "120%", marginTop: -5, marginBottom: 12 }}>
            <BarsOutlined /> DİĞER KURUM KARTLARIM
          </p>
        </Col>
      </Row>

      <Table
        rowSelection={rowSelection}
        columns={tableColumns}
        dataSource={tableData.data}
        loading={tableData.loading}
        rowKey="id"
        pagination={false}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Veri Yok"
            />
          ),
        }}
        style={{ overflowX: "auto", width: "100%" }}
      />

      {isOtherCard && (
        <Row style={{ marginTop: 16 }} gutter={[12, 12]}>
          {shouldRenderOtherCardSummary && simpleCardPreview && (
            <Col span={24}>
              <p style={{ fontSize: 16, fontWeight: 500, marginBottom: 12 }}>
                Diğer kurum kartlarınıza ait detay bilgileriniz :
              </p>
              <Card
                size="small"
                style={{ borderRadius: 6 }}
                bodyStyle={{ padding: "12px 16px" }}
              >
                <div
                  style={{ fontSize: 13, color: "#6b7280", marginBottom: 6 }}
                >
                  Dönem İçi Harcama
                </div>
                <div style={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2 }}>
                  {simpleCardPreview.periodSpend === "" ||
                  simpleCardPreview.periodSpend === null ||
                  simpleCardPreview.periodSpend === undefined
                    ? "-"
                    : ColumnHelper.renderDecimal(simpleCardPreview.periodSpend)}
                </div>
              </Card>
            </Col>
          )}

          {shouldRenderOtherCardTransactions && (
            <Col span={24}>
              {nonCreditCardDetail.loading || nonCreditTransactions.loading ? (
                <Row justify="center" style={{ padding: "24px 0" }}>
                  <Spin />
                </Row>
              ) : (
                <>
                  <Card
                    size="small"
                    style={{ borderRadius: 6, marginBottom: 12 }}
                    bodyStyle={{ padding: 16 }}
                  >
                    <Row gutter={[12, 12]}>
                      <Col xs={24} md={8}>
                        <label style={{ display: "block", marginBottom: 6 }}>
                          Başlangıç Tarihi
                        </label>
                        <DatePicker
                          allowClear={false}
                          value={
                            draftFilters.startDate
                              ? draftFilters.startDate
                              : null
                          }
                          format="DD.MM.YYYY"
                          style={{ width: "100%" }}
                          onChange={(date) =>
                            handleDraftFilterChange("startDate", date)
                          }
                        />
                      </Col>
                      <Col xs={24} md={8}>
                        <label style={{ display: "block", marginBottom: 6 }}>
                          Bitiş Tarihi
                        </label>
                        <DatePicker
                          allowClear={false}
                          value={
                            draftFilters.endDate ? draftFilters.endDate : null
                          }
                          format="DD.MM.YYYY"
                          style={{ width: "100%" }}
                          onChange={(date) =>
                            handleDraftFilterChange("endDate", date)
                          }
                        />
                      </Col>
                      <Col xs={24} md={8}>
                        <label style={{ display: "block", marginBottom: 6 }}>
                          İşlem Tipi
                        </label>
                        <Select
                          value={draftFilters.debitCreditIndicator}
                          onChange={(value) =>
                            handleDraftFilterChange(
                              "debitCreditIndicator",
                              value,
                            )
                          }
                          style={{ width: "100%" }}
                        >
                          <Select.Option value="">Hepsi</Select.Option>
                          <Select.Option value="B">Borç</Select.Option>
                          <Select.Option value="A">Alacak</Select.Option>
                        </Select>
                      </Col>
                    </Row>
                    <Row
                      justify="end"
                      gutter={[8, 8]}
                      style={{ marginTop: 12 }}
                    >
                      <Col>
                        <Button
                          type="primary"
                          onClick={handleSearchNonCreditTransactions}
                        >
                          Sorgula
                        </Button>
                      </Col>
                      <Col>
                        <Button onClick={handleResetNonCreditTransactions}>
                          Filtre Temizle
                        </Button>
                      </Col>
                    </Row>
                  </Card>

                  {renderInfoTable(
                    "Kart Hareketleri",
                    transactionColumns,
                    nonCreditTransactions.data,
                    {
                      pagination: {
                        current: nonCreditFilters.page,
                        pageSize: nonCreditFilters.pageSize,
                        total: nonCreditTransactions.total,
                        showSizeChanger: false,
                        onChange: (page) =>
                          setNonCreditFilters((prevState) => ({
                            ...prevState,
                            page,
                          })),
                      },
                    },
                  )}
                </>
              )}
            </Col>
          )}
        </Row>
      )}
      {shouldRenderCreditCardPanel && (
        <Row style={{ marginTop: 16 }} gutter={[12, 12]}>
          <Col span={24}>
            <Row justify="space-between" align="middle" gutter={[12, 12]}>
              <Col xs={24} md={12}>
                <p style={{ fontSize: 16, fontWeight: 500, marginBottom: 0 }}>
                  Kredi kartınıza ait detay bilgiler :
                </p>
              </Col>
              <Col xs={24} md={12} style={{ textAlign: "right" }}>
                <Select
                  value={selectedCurrency}
                  onChange={setSelectedCurrency}
                  style={{ minWidth: 140 }}
                >
                  {statementCurrencies.map((currency) => (
                    <Select.Option key={currency} value={currency}>
                      {currency}
                    </Select.Option>
                  ))}
                </Select>
              </Col>
            </Row>
          </Col>

          <Col span={24}>
            {creditCardDetail.loading ? (
              <Row justify="center" style={{ padding: "32px 0" }}>
                <Spin />
              </Row>
            ) : (
              <Row gutter={[12, 12]}>
                <Col xs={24} xl={12}>
                  {renderKeyValueInfoTable("Limit Bilgileriniz", [
                    {
                      key: "total-limit",
                      label: "Toplam Limit",
                      value: getDetailAmountWithCurrency(
                        ["totalLimitAmount", "TotalLimitAmount"],
                        ["totalLimitCurrency", "TotalLimitCurrency"],
                      ),
                    },
                    {
                      key: "available-limit",
                      label: "Kullanılabilir Limit",
                      value: getDetailAmountWithCurrency(
                        ["availableLimitAmount", "AvailableLimitAmount"],
                        ["availableLimitCurrency", "AvailableLimitCurrency"],
                      ),
                    },
                    {
                      key: "cash-withdrawal-limit",
                      label: "Nakit Çekim Limiti",
                      value: getDetailAmountWithCurrency(
                        [
                          "cashWithdrawalLimitAmount",
                          "CashWithdrawalLimitAmount",
                        ],
                        [
                          "cashWithdrawalLimitCurrency",
                          "CashWithdrawalLimitCurrency",
                        ],
                      ),
                    },
                    {
                      key: "available-cash-withdrawal-limit",
                      label: "Kullanılabilir Nakit Çekim Limiti",
                      value: getDetailAmountWithCurrency(
                        [
                          "availableCashWithdrawalLimitAmount",
                          "AvailableCashWithdrawalLimitAmount",
                        ],
                        [
                          "availableCashWithdrawalLimitCurrency",
                          "AvailableCashWithdrawalLimitCurrency",
                        ],
                      ),
                    },
                  ])}
                </Col>

                <Col xs={24} xl={12}>
                  {renderKeyValueInfoTable(
                    "Ekstre ve Borç Bilgileriniz",
                    [
                      {
                        key: "statement-debt",
                        label: "Ekstre Borcu",
                        value: getDetailAmountWithCurrency(
                          ["statementDebtAmount", "StatementDebtAmount"],
                          ["statementDebtCurrency", "StatementDebtCurrency"],
                        ),
                      },
                      {
                        key: "remaining-statement-debt",
                        label: "Kalan Ekstre Borcu",
                        value: getDetailAmountWithCurrency(
                          [
                            "remainingStatementDebtAmount",
                            "RemainingStatementDebtAmount",
                          ],
                          [
                            "remainingStatementDebtCurrency",
                            "RemainingStatementDebtCurrency",
                          ],
                        ),
                      },
                      {
                        key: "minimum-payment",
                        label: "Asgari Ödeme",
                        value: getDetailAmountWithCurrency(
                          ["minimumPaymentAmount", "MinimumPaymentAmount"],
                          ["minimumPaymentCurrency", "MinimumPaymentCurrency"],
                        ),
                      },
                      {
                        key: "remaining-minimum-payment",
                        label: "Kalan Asgari Ödeme",
                        value: getDetailAmountWithCurrency(
                          [
                            "remainingMinimumPaymentAmount",
                            "RemainingMinimumPaymentAmount",
                          ],
                          [
                            "remainingMinimumPaymentCurrency",
                            "RemainingMinimumPaymentCurrency",
                          ],
                        ),
                      },
                      {
                        key: "remaining-installment-total",
                        label: "Kalan Taksit",
                        value: getDetailAmountWithCurrency(
                          [
                            "remainingInstallmentTotalAmount",
                            "RemainingInstallmentTotalAmount",
                          ],
                          [
                            "remainingInstallmentTotalCurrency",
                            "RemainingInstallmentTotalCurrency",
                          ],
                        ),
                      },
                      {
                        key: "statement-date",
                        label: "Hesap Kesim Tarihi",
                        value: formatDetailDate(
                          getDetailField(["statementDate", "StatementDate"]),
                        ),
                      },
                      {
                        key: "payment-due-date",
                        label: "Son Ödeme Tarihi",
                        value: formatDetailDate(
                          getDetailField(["paymentDueDate", "PaymentDueDate"]),
                        ),
                      },
                    ],
                    {
                      footerAction:
                        selectedCard?.hasCardTransactionPermission ? (
                          <Button
                            type="primary"
                            onClick={() =>
                              redirectToCardStatementDetail(
                                selectedCard,
                                creditCardDetail.data,
                                selectedCurrency,
                              )
                            }
                          >
                            Ekstre Detay
                          </Button>
                        ) : null,
                    },
                  )}
                </Col>

                <Col xs={24} xl={12}>
                  {renderKeyValueInfoTable(
                    "Dönem İçi Harcama ve Ödemeleriniz",
                    [
                      {
                        key: "period-movement-total",
                        label: "Dönem İçi Hareket Toplamı",
                        value: getDetailAmountWithCurrency(
                          [
                            "periodMovementTotalAmount",
                            "PeriodMovementTotalAmount",
                          ],
                          [
                            "periodMovementTotalCurrency",
                            "PeriodMovementTotalCurrency",
                          ],
                        ),
                      },
                      {
                        key: "remaining-installment-total-amount",
                        label: "Kalan Toplam Taksit Tutarı",
                        value: getDetailAmountWithCurrency(
                          [
                            "remainingInstallmentTotalAmount",
                            "RemainingInstallmentTotalAmount",
                          ],
                          [
                            "remainingInstallmentTotalCurrency",
                            "RemainingInstallmentTotalCurrency",
                          ],
                        ),
                      },
                    ],
                    {
                      footerAction:
                        selectedCard?.hasCardTransactionPermission ? (
                          <Button
                            type="primary"
                            onClick={() =>
                              redirectToCardTransactions(
                                selectedCard,
                                creditCardDetail.data,
                                selectedCurrency,
                              )
                            }
                          >
                            Kart Hareketleri
                          </Button>
                        ) : null,
                    },
                  )}
                </Col>
                <Col xs={24} xl={12}>
                  {renderKeyValueInfoTable(
                    "Puanlarınız",
                    getCreditCardRewardPointRows(),
                  )}
                </Col>
              </Row>
            )}
          </Col>
        </Row>
      )}
    </>
  );
};

export default BkmTenantCardList;
